import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import { colors } from '../../../constants/colors';
import { FadeIn } from '../../../core/util/commonCssAnimations';
import { PinkPrimaryButton } from './BaseButton';

const borderColor = {
  initial: colors.crunchBerry,
  loading: colors.greyHounds,
  error: colors.crunchBerry,
  success: colors.grannySmith,
};

const backgroundColor = {
  initial: colors.crunchBerry,
  loading: 'transparent',
  error: colors.crunchBerry,
  success: colors.grannySmith,
};

const borderRadius = {
  initial: '3px',
  loading: '50px',
  error: '3px',
  success: '3px',
};

const width = {
  initial: '100%',
  loading: '50px',
  error: '100%',
  success: '100%',
};

const StyledButton = styled(PinkPrimaryButton)`
  transition: all 500ms ease;
  padding: 0;
  min-width: 53px;
  width: 100%;
  ${props => css`
    background-color: ${props.disabled ? 'rgba(0, 0, 0, 0.12)' : backgroundColor[props.status]};
    border: ${props.disabled ? 0 : '3px solid'};
    border-radius: ${borderRadius[props.status]};
    border-color: ${borderColor[props.status]};
    color: ${['success', 'loading'].includes(props.status) ? 'transparent' : colors.white};
    transition-delay: ${props.status === 'loading' ? 0 : '400ms'};
    box-shadow: none;
    &:hover {
      background-color: ${props.disabled ? 'rgba(0, 0, 0, 0.12)' : backgroundColor[props.status]};
    }
  `};
`;

const Root = styled.div`
  transition: width 500ms ease;
  text-align: center;
  position: relative;
  margin: 0 auto;
  ${props => css`
    width: ${width[props.status]};
    transition-delay: ${props.status === 'loading' ? 0 : '200ms'};
  `};
`;

const Spinner = styled(CircularProgress)`
  position: absolute;
  top: -4px;
  left: -4px;
`;

const CheckMark = styled.div`
  position: absolute;
  margin: auto;
  top: 0;
  left: 2px;
  right: 0;
  bottom: 3px;
  height: 10px;
  width: 18px;
  transform: rotate(-45deg);
  background-color: transparent;
  border-bottom-style: solid;
  border-left-style: solid;
  border-width: 3px;
  border-color: ${colors.white};
  border-radius: 1px;
  animation: ${FadeIn} 300ms linear;
`;

export const BUTTON_STATUS_INITIAL = 'initial';
export const BUTTON_STATUS_LOADING = 'loading';
export const BUTTON_STATUS_ERROR = 'error';
export const BUTTON_STATUS_SUCCESS = 'success';

export const buttonStatuses = [
  BUTTON_STATUS_INITIAL,
  BUTTON_STATUS_LOADING,
  BUTTON_STATUS_ERROR,
  BUTTON_STATUS_SUCCESS,
];

class TransitionButton extends Component {
  static propTypes = {
    text: PropTypes.string,
    status: PropTypes.oneOf(buttonStatuses),
    children: PropTypes.any,
  };

  static defaultProps = {
    status: 'initial',
  };

  constructor(props) {
    super(props);
    this.loadingTimeout = null;
  }

  state = {
    loading: false,
  };

  componentDidUpdate = prevProps => {
    const prevStatus = prevProps.status;
    const { status } = this.props;
    if (prevStatus !== 'loading' && status === 'loading') {
      this.loadingTimeout = setTimeout(() => this.setState({ loading: true }), 500);
    } else if (prevStatus === 'loading' && status !== 'loading') {
      clearTimeout(this.loadingTimeout);
      this.setState({ loading: false });
    }
  };

  componentWillUnmount() {
    clearTimeout(this.loadingTimeout);
  }

  render() {
    return (
      <Root status={this.props.status}>
        <StyledButton {...this.props} onClick={this.handleClick}>
          {this.props.text || this.props.children}
        </StyledButton>
        {this.state.loading && <Spinner size={61} style={{ color: colors.squid }} />}
        {!this.state.loading && this.props.status === 'success' && <CheckMark />}
      </Root>
    );
  }
}

export default TransitionButton;
